import React, { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import { ConfigProvider } from 'antd';

import { getProfile } from 'src/store/profile';
// import { getAdminSession } from 'src/store/adminAuth';

import { AppLayout, ThemeProvider } from 'src/hoc';

import {
  AboutUs,
  Agreement,
  Contacts,
  Home,
  News,
  PersonalAccount,
  ForgotPassword,
  ForgotPasswordRequested,
  Reviews,
  SignIn,
  SignUp,
  ViewNews,
  ForgotPasswordSuccess,
  CreateExchange,
  AcceptExchange,
  ViewExchange,
  ExchangeStatus,
  ApplicationExchanges,
  ExchangeDirections,
  AdminReviews,
  AdminReviewsForm,
  Currencies,
  CurrenciesForm,
  Users,
  UserForm,
  ApplicationForm,
  ExchangeDirectionsForm,
  ForgotPasswordConfirm,
  UserExchangeDirections,
  ExchangeWeGold,
  Admins,
  AdminsForm,
  CashExchanges,
  CashExchangesForm,
} from 'src/features';

import { ROUTE } from 'src/constants';

import { AdminLayout } from 'src/hoc/adminLayout/AdminLayout';
import { ProtectedAdminRoute, ProtectedRoute } from 'src/hoc/route';

import './app.scss';
import 'react-toastify/dist/ReactToastify.css';

export const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { pathname } = useLocation();

  useEffect(() => {
    if (!pathname.includes('dashboard')) {
      dispatch(getProfile());
    }
  }, [dispatch, pathname]);

  // useEffect(() => {
  //   if (pathname.includes('dashboard') && pathname !== ROUTE.ADMIN.LOGIN) {
  //     dispatch(getAdminSession());
  //   }
  // }, [dispatch, navigate, pathname]);

  useEffect(() => {
    const session = localStorage.getItem('admin-session');

    if (pathname === '/dashboard') {
      if (session === 'true') {
        navigate(ROUTE.ADMIN.ORDERS.MAIN);
      } else {
        navigate(ROUTE.ADMIN.LOGIN.MAIN);
      }
    }
  }, [navigate, pathname]);

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: '#04ba96' },
        components: { Button: { size: '', colorBgContainer: '#f2f5f8', colorFill: '#04ba96' } },
      }}
    >
      <ThemeProvider>
        <div className="app">
          <Routes>
            <Route element={<AppLayout />}>
              <Route path={ROUTE.HOME} element={<Home />} />
              <Route path={ROUTE.NEWS.MAIN} element={<News />} />
              <Route path={ROUTE.NEWS.VIEW_NEWS} element={<ViewNews />} />
              <Route path={ROUTE.REVIEWS} element={<Reviews />} />
              <Route path={ROUTE.ABOUT_US} element={<AboutUs />} />
              <Route path={ROUTE.AGREEMENT} element={<Agreement />} />
              <Route path={ROUTE.CONTACTS} element={<Contacts />} />
              <Route element={<ProtectedRoute />}>
                <Route path={ROUTE.PERSONAL_ACCOUNT} element={<PersonalAccount />} />
              </Route>

              <Route path={ROUTE.EXCHANGE.DIRECTIONS} element={<UserExchangeDirections />} />
              <Route path={ROUTE.EXCHANGE.CREATE} element={<CreateExchange />} />
              <Route path={ROUTE.EXCHANGE.ACCEPT} element={<AcceptExchange />} />
              <Route path={ROUTE.EXCHANGE.VIEW} element={<ViewExchange />} />
              <Route path={ROUTE.EXCHANGE.STATUS} element={<ExchangeStatus />} />

              <Route path={ROUTE.EXCHANGE.WE_GOLD} element={<ExchangeWeGold />} />

              <Route path={ROUTE.AUTH.SIGN_IN} element={<SignIn />} />
              <Route path={ROUTE.AUTH.SIGN_UP} element={<SignUp />} />
              <Route path={ROUTE.AUTH.FORGOT_PASSWORD} element={<ForgotPassword />} />
              <Route
                path={ROUTE.AUTH.FORGOT_PASSWORD_REQUESTED}
                element={<ForgotPasswordRequested />}
              />
              <Route
                path={ROUTE.AUTH.FORGOT_PASSWORD_CONFIRM}
                element={<ForgotPasswordConfirm />}
              />
              <Route
                path={ROUTE.AUTH.FORGOT_PASSWORD_SUCCESS}
                element={<ForgotPasswordSuccess />}
              />
            </Route>

            <Route path={ROUTE.ADMIN.LOGIN.MAIN} element={<SignIn />} />

            <Route element={<ProtectedAdminRoute />}>
              <Route element={<AdminLayout />}>
                <Route path={ROUTE.ADMIN.ORDERS.MAIN} element={<ApplicationExchanges />} />
                <Route path={ROUTE.ADMIN.ORDERS.CREATE} element={<ApplicationForm />} />
                <Route path={ROUTE.ADMIN.ORDERS.EDIT} element={<ApplicationForm />} />

                <Route
                  path={ROUTE.ADMIN.EXCHANGE_DIRECTION.MAIN}
                  element={<ExchangeDirections />}
                />
                <Route
                  path={ROUTE.ADMIN.EXCHANGE_DIRECTION.CREATE}
                  element={<ExchangeDirectionsForm />}
                />
                <Route
                  path={ROUTE.ADMIN.EXCHANGE_DIRECTION.EDIT}
                  element={<ExchangeDirectionsForm />}
                />

                <Route path={ROUTE.ADMIN.REVIEWS.MAIN} element={<AdminReviews />} />
                <Route path={ROUTE.ADMIN.REVIEWS.EDIT} element={<AdminReviewsForm />} />

                <Route path={ROUTE.ADMIN.CASH_EXCHANGE.MAIN} element={<CashExchanges />} />
                <Route path={ROUTE.ADMIN.CASH_EXCHANGE.CREATE} element={<CashExchangesForm />} />
                <Route path={ROUTE.ADMIN.CASH_EXCHANGE.EDIT} element={<CashExchangesForm />} />

                <Route path={ROUTE.ADMIN.CURRENCIES.MAIN} element={<Currencies />} />
                <Route path={ROUTE.ADMIN.CURRENCIES.CREATE} element={<CurrenciesForm />} />
                <Route path={ROUTE.ADMIN.CURRENCIES.EDIT} element={<CurrenciesForm />} />

                <Route path={ROUTE.ADMIN.USERS.MAIN} element={<Users />} />
                <Route path={ROUTE.ADMIN.USERS.CREATE} element={<UserForm />} />
                <Route path={ROUTE.ADMIN.USERS.EDIT} element={<UserForm />} />

                <Route path={ROUTE.ADMIN.ADMINS.MAIN} element={<Admins />} />
                <Route path={ROUTE.ADMIN.ADMINS.CREATE} element={<AdminsForm />} />
                <Route path={ROUTE.ADMIN.ADMINS.EDIT} element={<AdminsForm />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to={ROUTE.HOME} />} />
          </Routes>

          <ToastContainer
            position="bottom-center"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            pauseOnHover
            theme="light"
          />
        </div>
      </ThemeProvider>
    </ConfigProvider>
  );
};
